import React, { useRef, useState } from 'react';
import { useSessionStore } from '../../store/sessionStore';
import { download, exportPNG, exportSVG, processToPseudocode } from '../../utils/export';
import { validateProcessSegments } from '../../utils/processValidation';
import { canMutateWithIssues } from '../../utils/validationPolicy';
import { buildEdgeMap, resolveProcessEdges } from '../../utils/processEdges';

interface SaveSectionProps {
  svgRef: React.RefObject<SVGSVGElement | null>;
  onExportPseudocode?: () => void;
  onExportPNG?: () => void;
}

const SaveSection: React.FC<SaveSectionProps> = ({ svgRef, onExportPseudocode, onExportPNG }) => {
  const getState  = useSessionStore(s => s.getState);
  const loadState = useSessionStore(s => s.loadState);
  const fileRef   = useRef<HTMLInputElement>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [pseudocodePreview, setPseudocodePreview] = useState<string | null>(null);

  const processes = useSessionStore(s => s.processes);
  const activeProcessId = useSessionStore(s => s.activeProcessId);
  const nodes = useSessionStore(s => s.nodes);
  const edges = useSessionStore(s => s.edges);
  const edgeMap = buildEdgeMap(edges);

  const showFeedback = (message: string) => {
    setCopyFeedback(message);
    window.setTimeout(() => setCopyFeedback(null), 1800);
  };

  const saveJSON = () => {
    for (const proc of processes) {
      const validation = validateProcessSegments({ segments: resolveProcessEdges(proc, edgeMap) });
      const policy = canMutateWithIssues('session.persist', validation.issues, { processName: proc.name });
      if (!policy.canMutate) {
        alert(policy.blockingMessage ?? `No se pudo guardar el proceso "${proc.name}" por inconsistencias estructurales.`);
        return;
      }
    }

    const state = getState();
    const ts    = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
    download(`process-mapper_${ts}.json`, JSON.stringify(state, null, 2), 'application/json');
  };


  const handleExportSVG = () => {
    if (!svgRef.current) return;
    exportSVG(svgRef.current, activeProcessId ?? undefined, processes);
  };

  const handleExportPNG = () => {
    if (!svgRef.current) return;
    exportPNG(svgRef.current, activeProcessId ?? undefined, processes);
    onExportPNG?.();
  };

  const loadJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try { loadState(JSON.parse(ev.target?.result as string)); }
      catch (err) { alert('Error al leer el archivo JSON'); }
      if (fileRef.current) fileRef.current.value = '';
    };
    reader.readAsText(file);
  };

  // Comportamiento global elegido:
  // - si hay proceso activo, se copia ese proceso
  // - si no hay activo, se concatenan todos los procesos
  const openPseudocodePreview = () => {
    if (!processes.length) {
      showFeedback('No hay procesos para exportar');
      return;
    }

    const activeProcess = activeProcessId
      ? processes.find(p => p.id === activeProcessId)
      : undefined;
    const targets = activeProcess ? [activeProcess] : processes;
    const pseudo = targets
      .map(proc => processToPseudocode(proc, nodes, resolveProcessEdges(proc, edgeMap)))
      .join('\n\n---\n\n');

    setPseudocodePreview(pseudo);
    onExportPseudocode?.();
    showFeedback(activeProcess ? 'Vista previa del proceso activo abierta' : 'Vista previa de todos los procesos abierta');
  };

  const copyPseudocodeFromPreview = async () => {
    if (!pseudocodePreview) return;

    try {
      await navigator.clipboard.writeText(pseudocodePreview);
      showFeedback('Pseudocódigo copiado');
    } catch (err) {
      alert('No se pudo copiar al portapapeles. Copia manual:\n\n' + pseudocodePreview);
      showFeedback('No se pudo copiar automáticamente');
    }
  };

  const sbtn: React.CSSProperties = {
    flex: 1, padding: '7px 4px', border: '1px solid #e0dbd0', borderRadius: 7,
    background: '#fafaf8', fontFamily: "'Caveat',cursive", fontSize: 14, fontWeight: 600,
    color: '#666', cursor: 'pointer', textAlign: 'center',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.07em' }}>Guardar</p>
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={sbtn} onClick={saveJSON}>💾 JSON</button>
        <button style={sbtn} onClick={() => fileRef.current?.click()}>📂 Cargar</button>
        <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }} onChange={loadJSON} />
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={sbtn} onClick={handleExportSVG}>🧩 SVG</button>
        <button style={sbtn} onClick={handleExportPNG}>🖼️ PNG</button>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button style={sbtn} onClick={openPseudocodePreview} title="Abre una vista previa del pseudocódigo">
          {'{} '}Pseudocódigo
        </button>
        {copyFeedback && (
          <span style={{ fontSize: 10, color: '#777', fontFamily: "'DM Mono',monospace" }}>
            {copyFeedback}
          </span>
        )}
      </div>
      {pseudocodePreview && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa de pseudocódigo"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500,
            padding: 16,
          }}
          onClick={() => setPseudocodePreview(null)}
        >
          <div
            style={{
              width: 'min(920px, 100%)',
              maxHeight: '80vh',
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #e8e1d5',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: 14,
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.08em', color: '#8e8679' }}>
                Vista previa de pseudocódigo
              </p>
              <button
                type="button"
                onClick={() => setPseudocodePreview(null)}
                style={{
                  border: 'none',
                  background: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                  padding: 4,
                }}
                title="Cerrar"
              >
                ×
              </button>
            </div>
            <textarea
              readOnly
              value={pseudocodePreview}
              style={{
                width: '100%',
                minHeight: 280,
                height: '50vh',
                borderRadius: 8,
                border: '1px solid #e5e2da',
                padding: 10,
                resize: 'vertical',
                fontFamily: "'DM Mono',monospace",
                fontSize: 12,
                color: '#333',
                background: '#fcfcfb',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                style={{ ...sbtn, flex: '0 0 auto', minWidth: 120 }}
                onClick={copyPseudocodeFromPreview}
              >
                📋 Copiar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaveSection; 
